/**
 * SearchResults — dropdown overlay showing search results as entity cards.
 */

import type { JSX } from "react";
import { Icon } from "../ui";
import { EntityCardRenderer } from "../shared/EntityCardRenderer";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import type { SearchResultItem } from "./useSearch";

interface SearchResultsProps {
  readonly results: readonly SearchResultItem[];
  readonly isSearching: boolean;
  readonly query: string;
  readonly onSelect: (id: string, schemaId: string) => void;
  readonly onAgentSearch: (query: string) => void;
}

export function SearchResults({
  results,
  isSearching,
  query,
  onSelect,
  onAgentSearch,
}: SearchResultsProps): JSX.Element | null {
  const runtime = useAppRuntime();

  if (!query.trim()) return null;

  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-full max-w-[520px] z-50">
      <div className="rounded-xl bg-surface border border-edge shadow-xl max-h-[400px] overflow-y-auto">
        {/* Agent search — always first, compact single line */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAgentSearch(query); }}
          className="w-full flex items-center gap-2.5 px-3 py-2 text-left
            hover:bg-surface-hover transition-colors cursor-pointer border-none bg-transparent"
        >
          <Icon name="bot" size={15} className="shrink-0 text-accent" />
          <span className="text-[13px] text-content">Search with agent</span>
          <span className="text-[13px] text-content-tertiary truncate flex-1">{query}</span>
          <Icon name="arrow-right" size={13} className="shrink-0 text-content-tertiary/50" />
        </button>

        {/* Separator */}
        <div className="mx-3 border-b border-edge" />

        {isSearching && results.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-content-tertiary">
            <Icon name="loader" size={14} className="animate-spin" />
            <span>Searching…</span>
          </div>
        )}

        {!isSearching && results.length === 0 && query.trim() && (
          <div className="flex items-center justify-center py-6 text-sm text-content-tertiary">
            No results found
          </div>
        )}

        {results.length > 0 && (
          <div
            className="px-2 pb-2 flex flex-col gap-1.5"
            onClick={(e) => {
              // Prevent <a href> in entity cards from navigating —
              // selection is handled programmatically via onSelect.
              const anchor = (e.target as HTMLElement).closest("a");
              if (anchor) e.preventDefault();
            }}
          >
            {results.map((resultItem) => (
              <div
                key={resultItem.id}
                className="cursor-pointer"
                onClickCapture={() => { onSelect(resultItem.id, resultItem.schema_id); }}
              >
                <EntityCardRenderer
                  schemaId={resultItem.schema_id}
                  data={resultItem.data ?? { id: resultItem.id, name: resultItem.name }}
                  runtime={runtime}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
